/* © NXAPP Limited */
import { lazy, type ComponentType } from 'react'

const RELOAD_KEY = 'nxapp:chunk-reloaded-at'
const RELOAD_COOLDOWN_MS = 15_000

/**
 * After a deploy, an already-open tab still references the previous build's
 * hashed chunk names — those 404, and React surfaces
 * "Failed to fetch dynamically imported module".
 *
 * Retry once (covers a transient network blip), then force a reload to pick up
 * the new index.html. The sessionStorage stamp stops a reload loop if the chunk
 * is genuinely missing rather than merely stale.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function lazyWithReload<T extends ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  return lazy(async () => {
    try {
      return await factory()
    } catch (err) {
      try {
        return await factory()
      } catch {
        const last = Number(sessionStorage.getItem(RELOAD_KEY) ?? 0)
        if (Date.now() - last > RELOAD_COOLDOWN_MS) {
          sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
          window.location.reload()
          // Never resolves — the reload takes over.
          return await new Promise<{ default: T }>(() => {})
        }
        throw err
      }
    }
  })
}
